import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Loader2, Sparkles, CheckCircle2, Star, Printer, Library, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import type { ApparelStudy } from "@erc/shared";
import { ChartsCard } from "./ChartsCard";
import { BlankMockups } from "./BlankMockups";
import { SampleOrder } from "./SampleOrder";
import { MockupChooserModal, mockupHasArea, type ChooserMockup } from "../mockup/MockupChooserModal";
import { useStudioModel, customApi, studioTestDesigns, PRESENTATION_LABEL, PRODUCT_TYPE_LABEL, type CustomListingDetail, type StudioMockup } from "../../lib/customListing";

// Custom Liste Studyosu 5. adim: gorseller.
//  - Kolaj: ilham ana fotografindaki urunlerin baskisiz haline (bos sahne) 4. adimdaki
//    ornekler sirayla basilir; urun <-> deger eslesmesi ilhamdaki gibi korunur.
//  - Mockup kutuphanesi: listenin yapisina uyan mockuplar (analizden) onerilir,
//    secilenlere ornekler basilir.
//  - Ana gorsel: kolaj ya da basilan bir mockup (aramadaki kucuk resim).

export function ImagesStep({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const study = rec.study as ApparelStudy | null;
  const collage = rec.template.collage;
  const samples = rec.template.samples?.renders ?? [];
  const [model, setModel] = useStudioModel(rec);
  const [regen, setRegen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/collage`, { model, regenerateBlank: regen }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const running = collage?.status === "running";
  const main = study?.imageUrls?.[0] ?? rec.source.imageUrls?.[0];

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 p-4">
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Kolaj</h3>
          {collage?.status === "ready" && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Hazır</span>}
        </div>
        <p className="mb-3 text-xs text-gray-500">
          İlham fotoğrafındaki ürünlerin baskısız hali bir kez üretilir (yaklaşık 1 dakika, bir görsel ücreti); baskı alanları ilhamdaki gerçek baskılardan bulunur.
          Her alana 4. adımdaki örnekler sırayla basılır. Ürün ↔ değer eşleşmesi ilhamdaki gibi kalır; sırayı 4. adımdaki satırlardan değiştirebilirsin.
        </p>
        {samples.length === 0 ? (
          <p className="text-sm text-gray-500">Önce 4. adımda örnekleri bas.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="col-model" className="sr-only">Görsel modeli</label>
            <select id="col-model" value={model} onChange={(e) => setModel(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
              {rec.imageModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <button onClick={() => run.mutate()} disabled={run.isPending || running}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {run.isPending || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {running ? "Kolaj kuruluyor…" : collage?.status === "ready" ? "Kolajı yeniden bas" : "Kolajı oluştur"}
            </button>
            {collage?.blank && (
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" checked={regen} onChange={(e) => setRegen(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
                Boş sahneyi yeniden üret
              </label>
            )}
            <span className="text-xs text-gray-500">{collage?.blank ? "Boş sahne hazır; yeniden basmak ücretsiz." : `${samples.length} örnek`}</span>
          </div>
        )}
        {collage?.status === "failed" && <p className="mt-2 text-sm text-red-600">{collage.error ?? "Kolaj kurulamadı."}</p>}
        {(collage?.url || main) && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {main && (
              <figure>
                <img src={main} alt="İlham fotoğrafı" className="w-full rounded-lg border border-gray-200 object-contain" />
                <figcaption className="mt-1 text-xs text-gray-500">İlham</figcaption>
              </figure>
            )}
            {collage?.url && (
              <figure>
                <a href={collage.url} target="_blank" rel="noopener noreferrer" title="Tam boyutta aç">
                  <img src={collage.url} alt="Kolaj" className="w-full rounded-lg border border-gray-200 object-contain" />
                </a>
                <figcaption className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-500">
                  Bizim kolajımız
                  <MainImageButton rec={rec} value="collage" onChanged={onChanged} />
                </figcaption>
              </figure>
            )}
          </div>
        )}
        {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
      </div>

      {samples.length > 1 && (
        <div className="rounded-lg border border-gray-200 p-4">
          <SampleOrder rec={rec} onChanged={() => { setErr(null); onChanged(); }} onError={setErr}
            hint={rec.template.mockupRenders?.length || rec.listing.group?.id ? " (basılan mockuplar yeniden basılır)" : ""}
            afterSave={rec.template.mockupRenders?.length || rec.listing.group?.id ? () => customApi("POST", `/${rec.id}/rerender`) : undefined} />
        </div>
      )}
      <BlankMockups rec={rec} onChanged={onChanged} />
      <MockupLibrary rec={rec} onChanged={onChanged} />
      <ChartsCard rec={rec} onChanged={onChanged} />
    </div>
  );
}

function MainImageButton({ rec, value, onChanged }: { rec: CustomListingDetail; value: string; onChanged: () => void }) {
  const isMain = rec.listing.mainImage === value;
  const set = useMutation({ mutationFn: () => customApi("PATCH", `/${rec.id}`, { listing: { mainImage: isMain ? "" : value } }), onSuccess: onChanged });
  return (
    <button onClick={() => set.mutate()} disabled={set.isPending} aria-pressed={isMain}
      title={isMain ? "Ana görsel; kaldırmak için tıkla" : "Listenin ilk görseli (aramadaki küçük resim) yap"}
      className={clsx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium", isMain ? "bg-amber-50 text-amber-700" : "text-gray-500 hover:bg-gray-100")}>
      <Star className={clsx("h-3 w-3", isMain && "fill-current")} /> {isMain ? "Ana görsel" : "Ana görsel yap"}
    </button>
  );
}

// Kutuphanedeki mockuplar: listenin yapisina uyuma gore (alan sayisi, on/arka,
// sunum tipi ilhamla ayni mi, ton). Analizi olmayanlar en sonda; Mockup
// Yonetimi'nde AI ile analiz edilip onaylanabilir.
function MockupLibrary({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [showList, setShowList] = useState(false);
  const [chooser, setChooser] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { data, isLoading, error, refetch: refetchLib } = useQuery({
    queryKey: ["custom-mockups", rec.id],
    queryFn: () => customApi<{ inspiration: { presentation?: string; productType?: string }; mockups: StudioMockup[] }>("GET", `/${rec.id}/mockups`),
  });
  const hasSamples = (rec.template.samples?.renders?.length ?? 0) > 0;
  const print = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/mockup-renders`, { mockupIds: [...sel] }),
    onSuccess: () => { setErr(null); setSel(new Set()); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  // Satirda yalniz uyanlar; digerleri kutuphane penceresinden eklenir
  const list = (data?.mockups ?? []).filter((m) => m.match.score >= 3).slice(0, 48);
  const study = rec.study as ApparelStudy | null;
  const inspImg = study?.imageUrls?.[0] ?? rec.source.imageUrls?.[0];
  // Pencereden tiklanan mockup hemen orneklerle basilir ve "Basilan mockuplar"a eklenir
  const pickFromLibrary = async (m: ChooserMockup) => {
    if (!mockupHasArea(m)) { setMsg(`"${m.label}" mockupında baskı alanı yok; önce Mockup Yönetimi'nde alanını belirle.`); return; }
    setMsg(`"${m.label}" örneklerle basılıyor…`);
    try { await customApi("POST", `/${rec.id}/mockup-renders`, { mockupIds: [m.id] }); setMsg(`"${m.label}" basıldı.`); onChanged(); }
    catch (e) { setMsg((e as Error).message); }
  };
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const renders = rec.template.mockupRenders ?? [];
  const insp = data?.inspiration?.presentation;

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <h3 className="mb-1 text-sm font-semibold text-gray-900">Mockup kütüphanen</h3>
      <p className="mb-3 text-xs text-gray-500">
        Yalnız listeye uyan mockuplar gösterilir (uyuma göre sıralı): baskı alanı sayısı ilhamdaki ürün sayısına eşit mi, gerekli yüzler (ön/arka) var mı, sunum tipi ilhamla aynı mı{insp ? ` (ilham: ${PRESENTATION_LABEL[insp] ?? insp})` : ""}.
        Diğer mockuplar için "Kütüphaneden ekle". Analizi olmayan mockuplar <Link to="/mockup-manager" className="text-indigo-600 hover:underline">Mockup Yönetimi</Link>'nde AI ile analiz edilebilir.
      </p>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button onClick={() => setChooser(true)} disabled={!hasSamples}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          <Library className="h-4 w-4" /> Kütüphaneden ekle
        </button>
        <button onClick={() => print.mutate()} disabled={!sel.size || !hasSamples || print.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
          {print.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Seçilenlere örnekleri bas ({sel.size})
        </button>
        {!hasSamples && <span className="text-xs text-gray-500">Önce 4. adımda örnekleri bas.</span>}
      </div>
      {isLoading ? (
        <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Mockuplar yükleniyor…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{(error as Error).message}</p>
      ) : list.length > 0 && !showList ? (
        <button onClick={() => setShowList(true)} aria-expanded={false} className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600">
          <ChevronDown className="h-3.5 w-3.5" /> Uyan mockuplar ({list.length}){sel.size ? ` · ${sel.size} seçili` : ""}
        </button>
      ) : list.length === 0 ? (
        <p className="text-sm text-gray-500">Uyan mockup bulunamadı{(data?.mockups ?? []).some((m) => !m.analysis) ? ` (${(data?.mockups ?? []).filter((m) => !m.analysis).length} mockup analiz edilmemiş; sunum tipi uyumu yalnız analizli mockuplarda ölçülür)` : ""}. "Kütüphaneden ekle" ile istediğin mockupu seçebilirsin.</p>
      ) : (
        <div>
        <button onClick={() => setShowList(false)} aria-expanded className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600">
          <ChevronUp className="h-3.5 w-3.5" /> Uyan mockuplar ({list.length})
        </button>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {list.map((m) => (
            <button key={m.id} onClick={() => toggle(m.id)} aria-pressed={sel.has(m.id)}
              title={[...m.match.reasonsTr, ...m.match.warningsTr].join(" · ")}
              className={clsx("overflow-hidden rounded-lg border-2 text-left", sel.has(m.id) ? "border-indigo-500" : "border-gray-200 hover:border-gray-300")}>
              <img src={m.imageUrl} alt={m.label} loading="lazy" className="aspect-square w-full bg-gray-50 object-cover" />
              <div className="space-y-1 p-2">
                <p className="truncate text-xs font-medium text-gray-900">{m.label}</p>
                <div className="flex flex-wrap gap-1">
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{m.areaCount} alan</span>
                  {m.analysis ? (
                    <>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{PRESENTATION_LABEL[m.analysis.presentation] ?? m.analysis.presentation}</span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{PRODUCT_TYPE_LABEL[m.analysis.productType] ?? m.analysis.productType}</span>
                      {m.analysis.status === "approved" && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Onaylı</span>}
                    </>
                  ) : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Analiz yok</span>}
                </div>
                {m.match.warningsTr.length > 0 && <p className="truncate text-xs text-amber-700">{m.match.warningsTr[0]}</p>}
              </div>
            </button>
          ))}
        </div>
        </div>
      )}
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
      {chooser && (
        <MockupChooserModal
          title="Mockup ekle — tıkladığın mockup örneklerle basılıp “Basılan mockuplar”a eklenir"
          mode="multi" newestFirst selectedIds={renders.map((r) => r.mockupId)}
          inspiration={inspImg ? { imageUrl: inspImg, title: rec.name } : null}
          onPick={(m) => void pickFromLibrary(m)}
          onClose={() => { setChooser(false); setMsg(null); void refetchLib(); }}
          apparel={rec.isGarment !== false}
          {...studioTestDesigns(rec)}
        />
      )}
      {msg && chooser && <div className="fixed bottom-4 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-gray-900 px-4 py-2 text-sm text-white shadow-lg">{msg}</div>}

      {renders.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-xs font-semibold text-gray-700">Basılan mockuplar</h4>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {renders.map((r) => (
              <figure key={r.mockupId}>
                <a href={r.url} target="_blank" rel="noopener noreferrer"><img src={r.url} alt={r.label} loading="lazy" className="w-full rounded-lg border border-gray-200 object-contain" /></a>
                <figcaption className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-500">
                  <span className="truncate">{r.label}</span>
                  <span className="flex items-center gap-1">
                    <MainImageButton rec={rec} value={r.mockupId} onChanged={onChanged} />
                    <button onClick={() => { if (confirm(`"${r.label}" basımı silinsin mi? Liste görsellerinden de çıkar.`)) void customApi("DELETE", `/${rec.id}/mockup-renders/${r.mockupId}`).then(onChanged, (e) => setErr((e as Error).message)); }}
                      aria-label="Basılan mockupu sil" title="Sil" className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
